import { useState, useCallback, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Download, X, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import { fileUrl } from "@/lib/api";

// Worker matched to the bundled pdfjs-dist version (served from CDN)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function PdfViewer({ doc, onClose }) {
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(false);
  const [width, setWidth] = useState(Math.min(820, window.innerWidth - 48));

  const url = `${fileUrl(doc.url)}`;

  useEffect(() => {
    const onResize = () => setWidth(Math.min(820, window.innerWidth - 48));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setPage((p) => Math.min(p + 1, numPages || p));
      if (e.key === "ArrowLeft") setPage((p) => Math.max(p - 1, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [numPages, onClose]);

  const onLoad = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setPage(1);
    setError(false);
  }, []);

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex flex-col p-3 sm:p-4" data-testid="pdf-modal">
      <div className="flex items-center justify-between gap-3 mb-3 max-w-3xl w-full mx-auto">
        <h3 className="text-white font-heading font-bold truncate text-sm sm:text-base">{doc.title || "Document"}</h3>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={`${url}?download=1`}
            data-testid="pdf-download-btn"
            className="inline-flex items-center gap-1.5 bg-[#E11D2A] text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-[#c41320] transition-colors"
          >
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Download</span>
          </a>
          <button onClick={onClose} data-testid="pdf-modal-close" aria-label="Close" className="text-white/80 hover:text-white p-2">
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto overflow-auto rounded-lg bg-[#3a3a3a] flex justify-center" data-testid="pdf-canvas-wrap">
        {error ? (
          <div className="flex flex-col items-center justify-center text-center text-white/90 gap-3 p-8 m-auto">
            <AlertCircle className="h-8 w-8 text-amber-400" />
            <p className="font-semibold">This document couldn&apos;t be displayed.</p>
            <a href={`${url}?download=1`} className="inline-flex items-center gap-1.5 bg-[#E11D2A] text-white rounded-md px-4 py-2 text-sm font-semibold">
              <Download className="h-4 w-4" /> Download instead
            </a>
          </div>
        ) : (
          <Document
            file={url}
            onLoadSuccess={onLoad}
            onLoadError={() => setError(true)}
            loading={<div className="text-white/80 p-10">Loading document…</div>}
            className="py-4"
          >
            <Page
              pageNumber={page}
              width={width}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              className="shadow-2xl mx-auto"
            />
          </Document>
        )}
      </div>

      {numPages > 0 && !error && (
        <div className="flex items-center justify-center gap-4 mt-3 max-w-3xl w-full mx-auto" data-testid="pdf-pager">
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page <= 1}
            data-testid="pdf-prev-page"
            className="inline-flex items-center gap-1 rounded-md bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <span className="text-white text-sm font-medium tabular-nums" data-testid="pdf-page-indicator">
            Page {page} of {numPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(p + 1, numPages))}
            disabled={page >= numPages}
            data-testid="pdf-next-page"
            className="inline-flex items-center gap-1 rounded-md bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
